import { Card, CardBody, Flex, Spinner, Stack } from "@chakra-ui/react";
import CDS from "design-system";
import { useParams, useNavigate } from "react-router";
import NoSidebarScrollablePage from "layouts/NoSidebarScrollablePage";
import QueryError from "components/Error/QueryError";
import { CopyableValue } from "components/DataDisplay/Copyable";
import useCaminoStore from "stores/caminoStore";
import { generatePath } from "helpers/url";
import { SHARED_ROUTES } from "definitions/constants/routeConstants";
import { useTaskDetailQuery } from "features/Resources/Task/hooks";
import { useCustomFormQuery, useCustomFormResponseForTaskQuery } from "features/Resources/CustomForm/hooks";
import FormSubmittedView from "features/Resources/CustomForm/Components/FormSubmittedView";
import { CompleteFormTaskActionForm } from "features/Resources/Task/Components/Action/TaskActionForm/CompleteForm";

export default function FormResponsePage() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const currentUser = useCaminoStore((state) => state.currentUser);

  const { data: task, isLoading, error: queryError, isError } = useTaskDetailQuery(taskId);
  // Watch this on the page level so we can flip to the thank-you view the moment
  // a response exists — same query the inner form component reads.
  const { data: existingResponse } = useCustomFormResponseForTaskQuery(taskId);
  // Only needed for the post-submit copy — the fill flow fetches the form itself.
  const { data: form } = useCustomFormQuery(task?.customFormId ?? undefined);

  if (!taskId) {
    return (
      <NoSidebarScrollablePage>
        <CDS.Text14>Form not found.</CDS.Text14>
      </NoSidebarScrollablePage>
    );
  }

  if (isLoading) {
    return (
      <NoSidebarScrollablePage>
        <Flex justify="center" p={12}>
          <Spinner />
        </Flex>
      </NoSidebarScrollablePage>
    );
  }

  if (!task && isError) {
    return <QueryError queryError={queryError} resourceName="Task" id={taskId} />;
  }

  if (!task || task.taskAction !== "complete_form") {
    return (
      <NoSidebarScrollablePage>
        <CDS.Text14>This form is not available.</CDS.Text14>
      </NoSidebarScrollablePage>
    );
  }

  const submitted = !!existingResponse;
  // Fail open when the store hasn't hydrated — the backend policy still
  // rejects non-assignee submissions.
  const isAssignee = !currentUser?.id || String(task.assigneeId) === String(currentUser.id);

  if (!submitted && !isAssignee) {
    const formUrl = `${window.location.origin}${generatePath({
      path: SHARED_ROUTES.formResponse,
      params: { taskId },
    })}`;

    return (
      <NoSidebarScrollablePage>
        <Card>
          <CardBody>
            <Stack spacing={4} align="center" py={6}>
              <CDS.Heading20 textAlign="center">
                Only the assignee can fill out this form
              </CDS.Heading20>
              <CDS.Text14 color="fg.subtle" textAlign="center">
                This form can&apos;t be filled out by anyone except the assignee — please send this
                link to {task.assignee?.fullName ?? "the assignee"}
                {task.assignee?.workEmail ? ` (${task.assignee.workEmail})` : ""}.
              </CDS.Text14>
              <CopyableValue copyableValue={formUrl} maxW="lg" w="full" />
            </Stack>
          </CardBody>
        </Card>
      </NoSidebarScrollablePage>
    );
  }

  return (
    <NoSidebarScrollablePage>
      {submitted ? (
        <Card>
          <CardBody>
            <FormSubmittedView
              header={form?.responseScreenHeader}
              text={form?.responseScreenText}
              onGoHome={() => navigate("/")}
            />
          </CardBody>
        </Card>
      ) : (
        <Card>
          <CardBody>
            <CompleteFormTaskActionForm task={task} />
          </CardBody>
        </Card>
      )}
    </NoSidebarScrollablePage>
  );
}
